/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * Remote API implementation — connects the frontend to the Cloudflare Worker.
 * Implements IDataRepository + IModuleRepository so the entire app can switch
 * backends by changing one value in src/config/repository.ts:
 *
 *   export const REPO_TYPE: RepoType = 'REMOTE_API';
 */

import { IDataRepository, IModuleRepository } from '../domain/repository';
import { User, Notification, SystemLog, ApiKey, ApiSettings, Organization, Brand, Branch, Department, ModuleRequest, SubscriptionPlan, ModuleQuotaConfig, PlatformFeature, PlanFeatureValue, PlatformSettings, OrgPlan, PLAN_LIMITS } from '../domain/entities';
import { canViewMenuSettings, canViewRiderSettings } from '../core/security';
import { Rider, RiderSettings, WorkflowRequest } from '../modules/rider-ops/types';
import { Campaign, CampaignSettings, CampaignSubmission } from '../modules/aggregator/types';
import { DEFAULT_MENU_SETTINGS, type MenuSettings } from '../modules/menu-manage/types';

// ─── API Base URL ──────────────────────────────────────────────────────────

const API_BASE = (import.meta as any).env?.VITE_API_URL ?? 'http://localhost:8787';

// ─── Token management ──────────────────────────────────────────────────────

let _token: string | null = null;

const JSON_ARRAY_FIELDS = new Set([
  'enabled_apps',
  'enabled_modules',
  'provisioned_roles',
  'included_module_ids',
  'scopes_json',
  'assigned_branches',
  'assigned_modules',
  'assigned_brands',
  'assigned_departments',
  'tab_access',
]);

export function setAuthToken(token: string | null) {
  _token = token;
  if (token) {
    localStorage.setItem('dm_jwt', token);
  } else {
    localStorage.removeItem('dm_jwt');
  }
}

export function getAuthToken(): string | null {
  if (_token) return _token;
  _token = localStorage.getItem('dm_jwt');
  return _token;
}

export function hasApiStatus(error: unknown, ...statuses: number[]): boolean {
  if (!error || typeof error !== 'object' || !('status' in error)) return false;
  const status = Number((error as { status?: unknown }).status);
  return statuses.includes(status);
}

const DEFAULT_RIDER_SETTINGS: RiderSettings = {
  fileNoStart: 1000,
  fileNoPadding: 4,
  contracts: [],
  cardHeader: 'ClickIt Rider Card',
};

const DEFAULT_MENU_OPERATIONAL_CONFIG: Pick<MenuSettings, 'currency' | 'secondaryLanguage' | 'requireApproval'> = {
  currency: DEFAULT_MENU_SETTINGS.currency,
  secondaryLanguage: DEFAULT_MENU_SETTINGS.secondaryLanguage,
  requireApproval: !!DEFAULT_MENU_SETTINGS.requireApproval,
};

// ─── D1 snake_case ↔ JS camelCase conversion ──────────────────────────────

function snakeToCamel(obj: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(obj)) {
    // Convert snake_case → camelCase, then fix 2-letter language suffixes (_ar → AR, _en → EN)
    const camel = k.replace(/_([a-z])/g, (_, c) => c.toUpperCase()).replace(/(Ar|En)$/, m => m.toUpperCase());
    // Parse known JSON fields
    if (typeof v === 'string' && (k.endsWith('_json') || JSON_ARRAY_FIELDS.has(k))) {
      try { out[camel] = JSON.parse(v); } catch { out[camel] = v; }
    } else if (k === 'can_export_data') {
      out[camel] = v == null ? true : !!v;
    } else if (k === 'is_active' || k === 'must_change_password' || k === 'is_read' || k === 'access_restricted' || k === 'support_access_enabled' || k === 'manual_user_approval' || k === 'can_inspect_tenants' || k === 'is_system' || k === 'is_default' || k === 'ai_enabled' || k === 'ai_features_enabled') {
      out[camel] = !!v;
    } else {
      out[camel] = v;
    }
  }
  return out;
}

function camelToSnake(obj: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(obj)) {
    const snake = k.replace(/[A-Z]/g, (c) => `_${c.toLowerCase()}`);
    if (Array.isArray(v) && (k === 'enabledApps' || k === 'assignedBrands' || k === 'assignedDepartments' || k === 'scopes' || k === 'connectedModules')) {
      out[snake] = JSON.stringify(v);
    } else {
      out[snake] = v;
    }
  }
  return out;
}

function normalizeStringArray(value: unknown): string[] | undefined {
  if (value === undefined) return undefined;
  if (value === null) return [];
  if (Array.isArray(value)) return value.map(v => String(v));
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return [];
    try {
      const parsed = JSON.parse(trimmed);
      if (Array.isArray(parsed)) return parsed.map(v => String(v));
    } catch {
      // Fall through to comma-delimited parsing.
    }
    return trimmed.includes(',')
      ? trimmed.split(',').map(part => part.trim()).filter(Boolean)
      : [trimmed];
  }
  return [];
}

function normalizeNumericLimits(value: unknown): Record<string, number> {
  let source = value;
  if (typeof source === 'string') {
    try { source = JSON.parse(source); } catch { return {}; }
  }
  if (!source || typeof source !== 'object' || Array.isArray(source)) return {};

  const limits: Record<string, number> = {};
  for (const [key, raw] of Object.entries(source as Record<string, unknown>)) {
    if (raw === null || raw === undefined || raw === '') continue;
    const normalized = typeof raw === 'string' ? raw.trim().toLowerCase() : raw;
    const numeric = normalized === '∞' || normalized === 'infinity' || normalized === 'unlimited'
      ? -1
      : Number(raw);
    if (Number.isFinite(numeric)) limits[key] = numeric;
  }
  return limits;
}

function normalizeApiUser(user: User): User {
  return {
    ...user,
    canInspectTenants: user.canInspectTenants !== undefined ? !!user.canInspectTenants : undefined,
    canExportData: user.canExportData == null ? true : !!user.canExportData,
    assignedBranches: normalizeStringArray(user.assignedBranches) ?? [],
    assignedModules: normalizeStringArray(user.assignedModules) as User['assignedModules'],
    assignedBrands: normalizeStringArray(user.assignedBrands),
    tabAccess: normalizeStringArray(user.tabAccess),
    assignedDepartments: normalizeStringArray(user.assignedDepartments),
  };
}

function normalizeApiBranch(record: Record<string, unknown>): Branch {
  const branch = snakeToCamel(record) as Record<string, unknown>;
  return {
    id: String(branch.id ?? ''),
    name: String(branch.name ?? ''),
    code: String(branch.code ?? ''),
    brandId: String(branch.brandId ?? ''),
    orgId: typeof branch.orgId === 'string' ? branch.orgId : undefined,
  };
}

function normalizeApiBrand(record: Record<string, unknown>): Brand {
  const brand = snakeToCamel(record) as Record<string, unknown>;
  return {
    id: String(brand.id ?? ''),
    name: String(brand.name ?? ''),
    logoUrl: typeof brand.logoUrl === 'string' ? brand.logoUrl : undefined,
    orgId: typeof brand.orgId === 'string' ? brand.orgId : undefined,
    enabledModules: normalizeStringArray(brand.enabledModules) ?? [],
    branches: Array.isArray(brand.branches)
      ? brand.branches
          .filter((branch): branch is Record<string, unknown> => !!branch && typeof branch === 'object')
          .map(normalizeApiBranch)
      : [],
  };
}

function inferPlan(value?: unknown): OrgPlan | null {
  const normalized = String(value ?? '').trim().toUpperCase();
  if (!normalized) return null;
  if (normalized.includes('ENTERPRISE')) return OrgPlan.ENTERPRISE;
  if (normalized.includes('PRO')) return OrgPlan.PRO;
  if (normalized.includes('BASIC') || normalized.includes('STARTER')) return OrgPlan.BASIC;
  if (normalized.includes('FREE')) return OrgPlan.FREE;
  return null;
}

function normalizeApiOrganization(org: Organization & Record<string, unknown>): Organization {
  const logoUrl = typeof org.logoUrl === 'string' ? org.logoUrl : undefined;
  const faviconUrl = typeof org.faviconUrl === 'string' ? org.faviconUrl : undefined;
  const primaryColor = typeof org.primaryColor === 'string' ? org.primaryColor : undefined;
  return {
    ...org,
    enabledApps: normalizeStringArray(org.enabledApps) ?? [],
    provisionedRoles: normalizeStringArray(org.provisionedRoles) as Organization['provisionedRoles'],
    supportAccessEnabled: !!org.supportAccessEnabled,
    manualUserApproval: !!org.manualUserApproval,
    aiEnabled: org.aiEnabled !== undefined ? !!org.aiEnabled : undefined,
    maintenanceOverride: !!org.maintenanceOverride,
    whiteLabelSettings: org.whiteLabelSettings ?? (logoUrl || faviconUrl || primaryColor
      ? {
          logoUrl,
          faviconUrl,
          primaryColor,
        }
      : undefined),
  };
}

function normalizeSubscriptionPlan(record: Record<string, unknown>): SubscriptionPlan {
  const plan = snakeToCamel(record) as Record<string, unknown>;
  const inferredPlan = inferPlan(plan.name ?? plan.id);
  const usageLimits = {
    ...(inferredPlan ? PLAN_LIMITS[inferredPlan].usageLimits : {}),
    ...normalizeNumericLimits(plan.usageLimitsJson ?? plan.usageLimits),
  };
  return {
    id: String(plan.id ?? ''),
    name: String(plan.name ?? ''),
    price: Number(plan.price ?? 0),
    includedModuleIds: normalizeStringArray(plan.includedModuleIds),
    isSystem: !!plan.isSystem,
    isDefault: !!plan.isDefault,
    createdAt: Number(plan.createdAt ?? Date.now()),
    features: {
      apiAccess: !!plan.apiAccess,
      webhookAccess: !!plan.webhookAccess,
      analyticsAccess: !!plan.analyticsAccess,
      whiteLabelAccess: !!plan.whiteLabelAccess,
      systemLogsAccess: !!plan.systemLogsAccess,
      maintenanceAccess: !!plan.maintenanceAccess,
      marketScoutAccess: !!plan.marketScoutAccess,
      usageLimits,
    },
  };
}

function normalizeModuleQuota(record: Record<string, unknown>): ModuleQuotaConfig {
  const quota = snakeToCamel(record) as Record<string, unknown>;
  return {
    id: String(quota.id ?? ''),
    planId: String(quota.planId ?? ''),
    moduleId: String(quota.moduleId ?? ''),
    limits: normalizeNumericLimits(quota.limits ?? quota.limitsJson),
  };
}

async function putWithCreateFallback(path: string, createPath: string, body: Record<string, unknown>): Promise<void> {
  try {
    await apiFetch(path, { method: 'PUT', body: JSON.stringify(body) });
  } catch (error) {
    if ((error as ApiError).status === 404) {
      await apiFetch(createPath, { method: 'POST', body: JSON.stringify(body) });
      return;
    }
    throw error;
  }
}

// ─── Module → API endpoint mapping ────────────────────────────────────────

const MODULE_ENDPOINTS: Record<string, string> = {
  'rider-manager': '/api/riders',
  'aggregator': '/api/aggregator/campaigns',
  'campaign-manager': '/api/aggregator/campaigns',
  'brands': '/api/brands',
  'system:brands': '/api/brands',
  'system:departments': '/api/departments',
  'system:users': '/api/users',
  'system:logs': '/api/logs',
  'system:notifications': '/api/notifications',
  'system:api-keys': '/api/api-keys',
  'support-desk': '/api/support/tickets',
  'menu-profiles': '/api/menu/profiles',
  'menu-manage': '/api/menu/profiles',
  'modifier-library': '/api/menu/modifiers',
  'ministry-files': '/api/menu/vault',
  'menu-settings': '/api/menu/settings',
};

// ─── Menu Draft & Publish helpers ──────────────────────────────────────────

type MenuProfileSavePayload = {
  id: string;
  status?: string;
  statusReverted?: boolean;
  pendingCreationRequest?: boolean;
  requestId?: string;
  pendingChangeType?: 'CREATE_PROFILE';
  profile?: Record<string, unknown>;
};

function assertMenuProfileSavePayload(
  action: 'draft' | 'live',
  profileId: string,
  payload: MenuProfileSavePayload | undefined,
): MenuProfileSavePayload {
  if (!payload || typeof payload.id !== 'string' || payload.id.trim() === '') {
    throw new Error(`Menu ${action} save failed: server did not return the saved profile.`);
  }
  if (payload.id !== profileId) {
    throw new Error(`Menu ${action} save failed: server returned profile "${payload.id}" instead of "${profileId}".`);
  }
  return payload;
}

export async function saveMenuDraft(profileId: string, body: Record<string, unknown>): Promise<MenuProfileSavePayload> {
  const res = await apiFetch<{ data: MenuProfileSavePayload }>(`/api/menu/profiles/${profileId}/draft`, {
    method: 'PUT',
    body: JSON.stringify(body),
  });
  return assertMenuProfileSavePayload('draft', profileId, res.data);
}

export async function updateMenuLive(profileId: string, body: Record<string, unknown>): Promise<{ id: string; status: string; profile?: Record<string, unknown> }> {
  const res = await apiFetch<{ data: { id: string; status: string; profile?: Record<string, unknown> } }>(`/api/menu/profiles/${profileId}/live`, {
    method: 'PUT',
    body: JSON.stringify(body),
  });
  const payload = assertMenuProfileSavePayload('live', profileId, res.data);
  if (typeof payload.status !== 'string' || payload.status.trim() === '') {
    throw new Error('Menu live save failed: server did not return the new menu status.');
  }
  return { id: payload.id, status: payload.status, profile: payload.profile };
}

export async function pushMenuItemToProfiles(profileId: string, body: Record<string, unknown>): Promise<{ updatedCount: number }> {
  const res = await apiFetch<{ data: { updatedCount: number } }>(`/api/menu/profiles/${profileId}/push-item`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
  return res.data;
}

export async function propagateMenuEntityToProfiles(profileId: string, body: Record<string, unknown>): Promise<{ updatedCount: number }> {
  const res = await apiFetch<{ data: { updatedCount: number } }>(`/api/menu/profiles/${profileId}/propagate`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
  return res.data;
}

export interface MenuCopyTargetBrand {
  id: string;
  name: string;
  orgId?: string;
  orgName?: string;
}

export interface MenuCopyTargetOptionGroup {
  id: string;
  nameEN: string;
  nameAR?: string;
}

export interface MenuCopyTargetItem {
  id: string;
  nameEN: string;
  nameAR?: string;
  optionGroups: MenuCopyTargetOptionGroup[];
}

export interface MenuCopyTargetCategory {
  id: string;
  nameEN: string;
  nameAR?: string;
  items: MenuCopyTargetItem[];
}

export interface MenuCopyTargetProfile {
  id: string;
  name: string;
  brandId: string;
  brandName: string;
  orgId?: string;
  orgName?: string;
  status?: string;
  categoryCount?: number;
  itemCount?: number;
  placement?: MenuCopyTargetCategory[];
}

export interface MenuCopyTargetsPayload {
  brands: MenuCopyTargetBrand[];
  profiles: MenuCopyTargetProfile[];
}

export async function fetchMenuCopyTargets(): Promise<MenuCopyTargetsPayload> {
  const res = await apiFetch<{ data: MenuCopyTargetsPayload }>('/api/menu/copy-targets');
  return res.data;
}

export async function injectMenuCopy(targetProfileId: string, body: Record<string, unknown>): Promise<{ targetMenuName: string }> {
  const res = await apiFetch<{ data: { targetMenuName: string } }>(`/api/menu/profiles/${targetProfileId}/inject-copy`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
  return res.data;
}

export async function publishMenuLive(profileId: string): Promise<void> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 60_000);
  try {
    await apiFetch(`/api/menu/profiles/${profileId}/publish`, {
      method: 'POST',
      signal: controller.signal,
    });
  } finally {
    clearTimeout(timer);
  }
}

export async function discardMenuDraft(profileId: string): Promise<{ status: string }> {
  const res = await apiFetch<{ data: { id: string; status: string } }>(`/api/menu/profiles/${profileId}/discard-draft`, {
    method: 'POST',
  });
  return { status: res.data.status };
}

export async function unpublishMenu(profileId: string): Promise<{ status: string }> {
  const res = await apiFetch<{ data: { id: string; status: string } }>(`/api/menu/profiles/${profileId}/unpublish`, {
    method: 'POST',
  });
  return { status: res.data.status };
}

export async function cloneMenuProfile(sourceProfileId: string, name: string, tag?: string): Promise<{ id: string }> {
  const res = await apiFetch<{ data: { id: string } }>('/api/menu/clone-profile', {
    method: 'POST',
    body: JSON.stringify({ sourceProfileId, name, tag }),
  });
  return res.data;
}

// ─── Menu Change Requests ──────────────────────────────────────────────────

export async function submitChangeRequest(
  menuId: string, menuName: string, changes: string, previousData?: string,
  entityId?: string, entityType?: string, changeType?: string, entityData?: string,
): Promise<{ id: string }> {
  const res = await apiFetch<{ data: { id: string } }>('/api/menu/change-requests', {
    method: 'POST',
    body: JSON.stringify({ menuId, menuName, changes, previousData, entityId, entityType, changeType, entityData }),
  });
  return res.data;
}

export async function fetchChangeRequests(opts?: { status?: string; menuId?: string }): Promise<unknown[]> {
  const params = new URLSearchParams();
  if (opts?.status) params.set('status', opts.status);
  if (opts?.menuId) params.set('menuId', opts.menuId);
  const qs = params.toString();
  const res = await apiFetch<{ data: unknown[] }>(`/api/menu/change-requests${qs ? `?${qs}` : ''}`);
  return res.data;
}

export async function fetchChangeRequestDetail(id: string): Promise<unknown> {
  const res = await apiFetch<{ data: unknown }>(`/api/menu/change-requests/${id}`);
  return res.data;
}

export async function approveChangeRequest(id: string): Promise<{ nextStage?: string; nextStageId?: string } | null> {
  const res = await apiFetch<{ data?: { nextStage?: string; nextStageId?: string } }>(`/api/menu/change-requests/${id}/approve`, { method: 'POST' });
  return res.data ?? null;
}

export async function rejectChangeRequest(id: string, adminNote: string): Promise<void> {
  await apiFetch(`/api/menu/change-requests/${id}/reject`, {
    method: 'POST',
    body: JSON.stringify({ adminNote }),
  });
}

export async function fetchMenuSettings(user?: User | null): Promise<Record<string, unknown>> {
  if (!user || !canViewMenuSettings(user)) {
    return { ...DEFAULT_MENU_SETTINGS };
  }

  try {
    const res = await apiFetch<{ data: Record<string, unknown> }>('/api/menu/settings', {
      silentStatuses: [403],
    });
    return res.data ?? { ...DEFAULT_MENU_SETTINGS };
  } catch (error) {
    if (hasApiStatus(error, 403)) {
      return { ...DEFAULT_MENU_SETTINGS };
    }
    throw error;
  }
}

export async function fetchMenuConfig(_user?: User | null): Promise<Pick<MenuSettings, 'currency' | 'secondaryLanguage' | 'requireApproval'>> {
  try {
    const res = await apiFetch<{ data: Record<string, unknown> }>('/api/menu/config');
    return {
      currency: typeof res.data?.currency === 'string' ? res.data.currency : DEFAULT_MENU_OPERATIONAL_CONFIG.currency,
      secondaryLanguage: typeof res.data?.secondaryLanguage === 'string' ? res.data.secondaryLanguage : DEFAULT_MENU_OPERATIONAL_CONFIG.secondaryLanguage,
      requireApproval: !!res.data?.requireApproval,
    };
  } catch (error) {
    if (hasApiStatus(error, 403)) {
      return { ...DEFAULT_MENU_OPERATIONAL_CONFIG };
    }
    throw error;
  }
}

export async function fetchMenuOperationalConfig(user?: User | null): Promise<Partial<MenuSettings>> {
  if (user && canViewMenuSettings(user)) {
    return fetchMenuSettings(user) as Promise<Partial<MenuSettings>>;
  }
  return fetchMenuConfig(user);
}

export async function saveMenuSettings(settings: Record<string, unknown>): Promise<void> {
  await apiFetch('/api/menu/settings', {
    method: 'PUT',
    body: JSON.stringify(settings),
  });
}

// ─── Fetch helper ──────────────────────────────────────────────────────────

class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message);
    this.name = 'ApiError';
  }
}

type ApiFetchOptions = RequestInit & {
  silentStatuses?: number[];
};

export async function apiFetch<T = unknown>(
  path: string,
  options: ApiFetchOptions = {},
): Promise<T> {
  const { silentStatuses = [], ...requestOptions } = options;
  const token = getAuthToken();
  const headers: Record<string, string> = {
    ...(requestOptions.headers as Record<string, string> ?? {}),
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  if (requestOptions.body && typeof requestOptions.body === 'string') {
    headers['Content-Type'] = 'application/json';
  }

  const url = `${API_BASE}${path}`;

  let res: Response;
  try {
    res = await fetch(url, { ...requestOptions, headers });
  } catch (err) {
    console.error(`[apiFetch] Network error: ${requestOptions.method ?? 'GET'} ${path}`, err);
    throw new ApiError(0, 'Server is offline — please check your connection.');
  }

  if (res.status === 401) {
    setAuthToken(null);
    if (typeof window !== 'undefined') {
      const pathname = window.location.pathname;
      const isPublicRoute =
        ['/', '/landing', '/login', '/signup', '/landing-preview'].includes(pathname)
        || pathname.startsWith('/public-preview');
      if (!isPublicRoute) {
        window.location.replace('/login');
      }
    }
    throw new ApiError(401, 'Session expired. Please log in again.');
  }

  if (!res.ok) {
    const body = await res.json().catch(() => ({ message: res.statusText }));
    const msg = (body as any).message ?? `API error ${res.status}`;
    const sqlError = (body as any).error ?? (body as any).sqlError ?? null;
    // Detailed console logging for debugging — shows exact D1/SQL errors locally
    if (!silentStatuses.includes(res.status)) {
      console.error(
        `[apiFetch] ${res.status} ${requestOptions.method ?? 'GET'} ${path}\n` +
        `  Message: ${msg}` +
        (sqlError ? `\n  SQL Error: ${typeof sqlError === 'string' ? sqlError : JSON.stringify(sqlError)}` : '') +
        ((body as any).cause ? `\n  Cause: ${(body as any).cause}` : ''),
        body,
      );
    }
    throw new ApiError(res.status, msg);
  }

  return res.json() as Promise<T>;
}

// ─── Typed API response ────────────────────────────────────────────────────

interface ApiResponse<T> {
  data: T;
  message?: string;
}

// ═══════════════════════════════════════════════════════════════════════════════
// ApiModuleRepository
// ═══════════════════════════════════════════════════════════════════════════════

export class ApiModuleRepository implements IDataRepository, IModuleRepository {

  // ─── Overloaded: getAll ──────────────────────────────────────────────────
  // Overload 1 — IModuleRepository (no args → Riders):
  getAll(): Promise<Rider[]>;
  // Overload 2 — IDataRepository (moduleId + optional orgId):
  getAll<T>(moduleId: string, orgId?: string): Promise<T[]>;
  async getAll<T = Rider>(moduleId?: string, _orgId?: string): Promise<any> {
    if (moduleId === undefined) {
      // IModuleRepository path — return riders
      const res = await apiFetch<ApiResponse<Record<string, unknown>[]>>('/api/riders');
      return (res.data ?? []).map(r => snakeToCamel(r) as unknown as Rider);
    }
    // IDataRepository path
    const endpoint = MODULE_ENDPOINTS[moduleId];
    if (!endpoint) return [];
    const headers = _orgId ? { 'X-Org-Context': _orgId } : undefined;
    const res = await apiFetch<ApiResponse<Record<string, unknown>[]>>(endpoint, { headers });

    // Guard: if the endpoint returns a singleton object instead of an array, wrap it
    if (res.data && !Array.isArray(res.data)) {
      return [snakeToCamel(res.data as unknown as Record<string, unknown>) as T];
    }

    if (moduleId === 'system:brands') {
      return (res.data ?? []).map(r => normalizeApiBrand(r) as unknown as T);
    }

    return (res.data ?? []).map(r => snakeToCamel(r) as T);
  }

  // ─── Overloaded: getById ─────────────────────────────────────────────────
  // Overload 1 — IModuleRepository:
  getById(id: string): Promise<Rider | null>;
  // Overload 2 — IDataRepository:
  getById<T>(moduleId: string, id: string, orgId?: string): Promise<T | null>;
  async getById<T = Rider>(idOrModuleId: string, idOrUndefined?: string, _orgId?: string): Promise<any> {
    if (idOrUndefined === undefined) {
      // IModuleRepository path — get rider by id
      try {
        const res = await apiFetch<ApiResponse<Record<string, unknown>>>(`/api/riders/${idOrModuleId}`);
        return res.data ? snakeToCamel(res.data) as unknown as Rider : null;
      } catch (e) { if ((e as ApiError).status === 404) return null; throw e; }
    }
    // IDataRepository path
    const endpoint = MODULE_ENDPOINTS[idOrModuleId];
    if (!endpoint) return null;
    const headers = _orgId ? { 'X-Org-Context': _orgId } : undefined;
    try {
      const res = await apiFetch<ApiResponse<Record<string, unknown>>>(`${endpoint}/${idOrUndefined}`, { headers });
      return res.data ? snakeToCamel(res.data) as T : null;
    } catch (e) { if ((e as ApiError).status === 404) return null; throw e; }
  }

  async getByFileNumber(fileNumber: number): Promise<Rider | null> {
    const all = await this.getAll();
    return all.find((r: Rider) => r.fileNumber === fileNumber) ?? null;
  }

  // ─── Overloaded: save ────────────────────────────────────────────────────
  // Overload 1 — IModuleRepository:
  save(rider: Rider): Promise<void>;
  // Overload 2 — IDataRepository:
  save<T>(moduleId: string, orgId: string, data: T): Promise<T>;
  async save<T>(riderOrModuleId: Rider | string, orgIdParam?: string, dataParam?: T): Promise<any> {
    if (typeof riderOrModuleId === 'object') {
      // IModuleRepository path — save rider
      const rider = riderOrModuleId;
      const body = camelToSnake(rider as unknown as Record<string, unknown>);
      if (rider.id) {
        await apiFetch(`/api/riders/${rider.id}`, { method: 'PUT', body: JSON.stringify(body) });
      } else {
        await apiFetch('/api/riders', { method: 'POST', body: JSON.stringify(body) });
      }
      return;
    }
    // IDataRepository path
    const moduleId = riderOrModuleId;
    const data = dataParam as any;
    const endpoint = MODULE_ENDPOINTS[moduleId];
    if (!endpoint) throw new ApiError(400, `Unknown module: ${moduleId}`);
    const body = camelToSnake(data as Record<string, unknown>);
    const headers = orgIdParam ? { 'X-Org-Context': orgIdParam } : undefined;
    if (data.id) {
      const res = await apiFetch<ApiResponse<Record<string, unknown>>>(`${endpoint}/${data.id}`, {
        method: 'PUT', headers, body: JSON.stringify(body),
      });
      return (res.data ? snakeToCamel(res.data) : data) as T;
    }
    const res = await apiFetch<ApiResponse<Record<string, unknown>>>(endpoint, {
      method: 'POST', headers, body: JSON.stringify(body),
    });
    return (res.data ? snakeToCamel(res.data) : data) as T;
  }

  async update<T>(moduleId: string, _orgId: string, id: string, partial: Partial<T>): Promise<T> {
    const endpoint = MODULE_ENDPOINTS[moduleId];
    if (!endpoint) throw new ApiError(400, `Unknown module: ${moduleId}`);
    const body = camelToSnake(partial as Record<string, unknown>);
    const headers = _orgId ? { 'X-Org-Context': _orgId } : undefined;
    const res = await apiFetch<ApiResponse<Record<string, unknown>>>(`${endpoint}/${id}`, {
      method: 'PATCH', headers, body: JSON.stringify(body),
    });
    return (res.data ? snakeToCamel(res.data) : partial) as T;
  }

  // ─── Overloaded: delete ──────────────────────────────────────────────────
  // Overload 1 — IModuleRepository (1 param):
  delete(id: string): Promise<void>;
  // Overload 2 — IDataRepository (3 params):
  delete(moduleId: string, orgId: string, id: string): Promise<void>;
  async delete(idOrModuleId: string, _orgId?: string, itemId?: string): Promise<void> {
    if (itemId === undefined) {
      // IModuleRepository path — delete rider
      await apiFetch(`/api/riders/${idOrModuleId}`, { method: 'DELETE' });
      return;
    }
    // IDataRepository path
    const endpoint = MODULE_ENDPOINTS[idOrModuleId];
    if (!endpoint) return;
    const headers = _orgId ? { 'X-Org-Context': _orgId } : undefined;
    await apiFetch(`${endpoint}/${itemId}`, { method: 'DELETE', headers });
  }

  async getSettings(user?: User | null): Promise<RiderSettings> {
    if (!user || !canViewRiderSettings(user)) {
      return DEFAULT_RIDER_SETTINGS;
    }

    try {
      const res = await apiFetch<ApiResponse<Record<string, unknown>>>('/api/riders/settings', {
        silentStatuses: [403],
      });
      if (!res.data) return DEFAULT_RIDER_SETTINGS;
      const d = snakeToCamel(res.data) as any;
      return {
        fileNoStart: d.fileNoStart ?? 1000,
        fileNoPadding: d.fileNoPadding ?? 4,
        contracts: Array.isArray(d.contractsJson) ? d.contractsJson : [],
        cardHeader: d.cardHeader ?? DEFAULT_RIDER_SETTINGS.cardHeader,
      };
    } catch (error) {
      if (hasApiStatus(error, 403)) {
        return DEFAULT_RIDER_SETTINGS;
      }
      return DEFAULT_RIDER_SETTINGS;
    }
  }

  async saveSettings(settings: RiderSettings): Promise<void> {
    await apiFetch('/api/riders/settings', {
      method: 'PUT',
      body: JSON.stringify({
        fileNoStart: settings.fileNoStart,
        fileNoPadding: settings.fileNoPadding,
        contracts: settings.contracts,
        cardHeader: settings.cardHeader,
      }),
    });
  }

  // ── Workflow Requests ─────────────────────────────────────────────────────

  async getRequests(): Promise<WorkflowRequest[]> {
    const res = await apiFetch<ApiResponse<Record<string, unknown>[]>>('/api/riders/requests');
    return (res.data ?? []).map(r => {
      const c = snakeToCamel(r) as any;
      if (c.riderDataJson && typeof c.riderDataJson === 'string') {
        try { c.riderData = JSON.parse(c.riderDataJson); } catch { /* skip */ }
      }
      return c as WorkflowRequest;
    });
  }

  async saveRequest(request: WorkflowRequest): Promise<void> {
    const body = camelToSnake(request as unknown as Record<string, unknown>);
    await apiFetch('/api/riders/requests', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  // ── Notifications ─────────────────────────────────────────────────────────

  async getNotifications(userId: string): Promise<Notification[]> {
    const res = await apiFetch<ApiResponse<Record<string, unknown>[]>>('/api/notifications');
    return (res.data ?? []).map(r => snakeToCamel(r) as unknown as Notification);
  }

  async saveNotification(notification: Notification): Promise<void> {
    const body = camelToSnake(notification as unknown as Record<string, unknown>);
    await apiFetch('/api/notifications', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  async markNotificationRead(id: string): Promise<void> {
    await apiFetch(`/api/notifications/${id}/read`, { method: 'PATCH' });
  }

  async clearNotifications(): Promise<void> {
    await apiFetch('/api/notifications', { method: 'DELETE' });
  }

  // ── Auth / Current User ───────────────────────────────────────────────────

  private _currentUser: User | null = null;

  async getCurrentUser(): Promise<User | null> {
    if (this._currentUser) return this._currentUser;
    const res = await apiFetch<{ user: Record<string, unknown> }>('/api/auth/me');
    this._currentUser = normalizeApiUser(snakeToCamel(res.user) as unknown as User);
    return this._currentUser;
  }

  async setCurrentUser(user: User): Promise<void> {
    this._currentUser = normalizeApiUser(user);
  }

  /** Clear the in-memory user cache so the next getCurrentUser() call re-fetches from the API. */
  clearCurrentUser(): void {
    this._currentUser = null;
  }

  async getDemoUsers(): Promise<User[]> {
    const currentUser = await this.getCurrentUser().catch(() => null);
    return currentUser ? [currentUser] : [];
  }

  async getAllUsersForLogin(): Promise<User[]> {
    // Public endpoint not available; return empty — login uses /api/auth/login instead
    return [];
  }

  // ── System Logs ───────────────────────────────────────────────────────────

  async getSystemLogs(): Promise<SystemLog[]> {
    const res = await apiFetch<ApiResponse<Record<string, unknown>[]>>('/api/logs');
    return (res.data ?? []).map(r => snakeToCamel(r) as unknown as SystemLog);
  }

  async saveSystemLog(log: SystemLog): Promise<void> {
    const body = camelToSnake(log as unknown as Record<string, unknown>);
    await apiFetch('/api/logs', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  // ── User Management ───────────────────────────────────────────────────────

  async getUsers(): Promise<User[]> {
    const res = await apiFetch<ApiResponse<Record<string, unknown>[]>>('/api/users');
    return (res.data ?? []).map(r => normalizeApiUser(snakeToCamel(r) as unknown as User));
  }

  async saveUser(user: User): Promise<void> {
    const body = camelToSnake(user as unknown as Record<string, unknown>);
    if (user.id) {
      await putWithCreateFallback(`/api/users/${user.id}`, '/api/users', body);
    } else {
      await apiFetch('/api/users', { method: 'POST', body: JSON.stringify(body) });
    }
    if (this._currentUser?.id === user.id) {
      this._currentUser = normalizeApiUser({ ...this._currentUser, ...user });
    }
  }

  async deleteUser(id: string): Promise<void> {
    await apiFetch(`/api/users/${id}`, { method: 'DELETE' });
  }

  // ── API Keys ──────────────────────────────────────────────────────────────

  async getApiKeys(): Promise<ApiKey[]> {
    const res = await apiFetch<ApiResponse<Record<string, unknown>[]>>('/api/api-keys');
    return (res.data ?? []).map(r => snakeToCamel(r) as unknown as ApiKey);
  }

  async saveApiKey(key: ApiKey): Promise<void> {
    const body = camelToSnake(key as unknown as Record<string, unknown>);
    await apiFetch('/api/api-keys', { method: 'POST', body: JSON.stringify(body) });
  }

  async revokeApiKey(id: string): Promise<void> {
    await apiFetch(`/api/api-keys/${id}/revoke`, { method: 'PATCH' });
  }

  async deleteApiKey(id: string): Promise<void> {
    await apiFetch(`/api/api-keys/${id}`, { method: 'DELETE' });
  }

  // ── API Settings ──────────────────────────────────────────────────────────

  async getApiSettings(): Promise<ApiSettings> {
    try {
      const res = await apiFetch<ApiResponse<Record<string, unknown>>>('/api/api-settings');
      return snakeToCamel(res.data) as unknown as ApiSettings;
    } catch {
      return { rateLimitPerMinute: 60, webhooks: [], allowedOrigins: [] } as unknown as ApiSettings;
    }
  }

  async saveApiSettings(settings: ApiSettings): Promise<void> {
    const body = camelToSnake(settings as unknown as Record<string, unknown>);
    await apiFetch('/api/api-settings', { method: 'PUT', body: JSON.stringify(body) });
  }

  // ── Organizations ─────────────────────────────────────────────────────────

  async getOrganizations(): Promise<Organization[]> {
    const res = await apiFetch<ApiResponse<Record<string, unknown>[]>>('/api/orgs');
    return (res.data ?? []).map(r => normalizeApiOrganization(snakeToCamel(r) as unknown as Organization & Record<string, unknown>));
  }

  async getOrganizationById(id: string): Promise<Organization | null> {
    try {
      const res = await apiFetch<ApiResponse<Record<string, unknown>>>(`/api/orgs/${id}`);
      return res.data ? normalizeApiOrganization(snakeToCamel(res.data) as unknown as Organization & Record<string, unknown>) : null;
    } catch { return null; }
  }

  /**
   * Fetches the current user's own organization via /api/auth/my-org.
   * Does NOT require saas:manage_orgs — any authenticated user can call it.
   */
  async getMyOrganization(): Promise<Organization | null> {
    try {
      const res = await apiFetch<ApiResponse<Record<string, unknown>>>('/api/auth/my-org');
      return res.data ? normalizeApiOrganization(snakeToCamel(res.data) as unknown as Organization & Record<string, unknown>) : null;
    } catch { return null; }
  }

  async saveOrganization(org: Organization): Promise<void> {
    const body = camelToSnake(org as unknown as Record<string, unknown>);
    if (org.id) {
      await putWithCreateFallback(`/api/orgs/${org.id}`, '/api/orgs', body);
    } else {
      await apiFetch('/api/orgs', { method: 'POST', body: JSON.stringify(body) });
    }
  }

  async deleteOrganization(id: string): Promise<void> {
    await apiFetch(`/api/orgs/${id}`, { method: 'DELETE' });
  }

  // ── Org Context ───────────────────────────────────────────────────────────

  private _currentOrgId: string | null = null;

  async getCurrentOrgId(): Promise<string | null> {
    if (this._currentOrgId) return this._currentOrgId;
    // Derive from JWT
    const token = getAuthToken();
    if (!token) return null;
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.org ?? null;
    } catch { return null; }
  }

  async setCurrentOrgId(orgId: string | null): Promise<void> {
    this._currentOrgId = orgId;
  }

  // ── God-Mode: explicit org-scoped reads ───────────────────────────────────

  async getRidersForOrg(orgId: string): Promise<Rider[]> {
    const res = await apiFetch<ApiResponse<Record<string, unknown>[]>>('/api/riders', {
      headers: { 'X-Org-Context': orgId },
    });
    return (res.data ?? []).map(r => snakeToCamel(r) as unknown as Rider);
  }

  async getSettingsForOrg(orgId: string): Promise<RiderSettings> {
    try {
      const res = await apiFetch<ApiResponse<Record<string, unknown>>>('/api/riders/settings', {
        headers: { 'X-Org-Context': orgId },
      });
      if (!res.data) return DEFAULT_RIDER_SETTINGS;
      const d = snakeToCamel(res.data) as any;
      return {
        fileNoStart: d.fileNoStart ?? 1000,
        fileNoPadding: d.fileNoPadding ?? 4,
        contracts: Array.isArray(d.contractsJson) ? d.contractsJson : [],
        cardHeader: d.cardHeader ?? DEFAULT_RIDER_SETTINGS.cardHeader,
      };
    } catch {
      return DEFAULT_RIDER_SETTINGS;
    }
  }

  async getWorkflowRequestsForOrg(orgId: string): Promise<WorkflowRequest[]> {
    const res = await apiFetch<ApiResponse<Record<string, unknown>[]>>('/api/riders/requests', {
      headers: { 'X-Org-Context': orgId },
    });
    return (res.data ?? []).map(r => snakeToCamel(r) as unknown as WorkflowRequest);
  }

  async getUsersForOrg(orgId: string): Promise<User[]> {
    const res = await apiFetch<ApiResponse<Record<string, unknown>[]>>('/api/users', {
      headers: { 'X-Org-Context': orgId },
    });
    return (res.data ?? []).map(r => normalizeApiUser(snakeToCamel(r) as unknown as User));
  }

  // ── Campaigns (Aggregator) ────────────────────────────────────────────────

  async getCampaigns(): Promise<Campaign[]> {
    const res = await apiFetch<ApiResponse<Record<string, unknown>[]>>('/api/aggregator/campaigns');
    return (res.data ?? []).map(r => snakeToCamel(r) as unknown as Campaign);
  }

  async saveCampaign(campaign: Campaign): Promise<void> {
    const body = camelToSnake(campaign as unknown as Record<string, unknown>);
    if (campaign.id) {
      await apiFetch(`/api/aggregator/campaigns/${campaign.id}`, { method: 'PUT', body: JSON.stringify(body) });
    } else {
      await apiFetch('/api/aggregator/campaigns', { method: 'POST', body: JSON.stringify(body) });
    }
  }

  async deleteCampaign(id: string): Promise<void> {
    await apiFetch(`/api/aggregator/campaigns/${id}`, { method: 'DELETE' });
  }

  async getCampaignSettings(): Promise<CampaignSettings> {
    try {
      const res = await apiFetch<ApiResponse<Record<string, unknown>>>('/api/aggregator/settings');
      const s = snakeToCamel(res.data) as unknown as CampaignSettings;
      return { ...s, aggregators: s.aggregators ?? [], aggregatorLogos: s.aggregatorLogos ?? {}, brands: s.brands ?? [] };
    } catch {
      return { aggregators: [], brands: [] } as CampaignSettings;
    }
  }

  async saveCampaignSettings(settings: CampaignSettings): Promise<void> {
    const body = camelToSnake(settings as unknown as Record<string, unknown>);
    await apiFetch('/api/aggregator/settings', { method: 'PUT', body: JSON.stringify(body) });
  }

  // ── Campaign Submissions ──────────────────────────────────────────────────

  async getCampaignSubmissions(): Promise<CampaignSubmission[]> {
    const res = await apiFetch<ApiResponse<Record<string, unknown>[]>>('/api/aggregator/submissions');
    return (res.data ?? []).map(r => {
      const c = snakeToCamel(r) as any;
      // Items now come as a pre-parsed array from the server — convert each item's keys
      if (Array.isArray(c.items)) {
        c.items = c.items.map((it: any) => typeof it === 'object' && it !== null ? snakeToCamel(it as Record<string, unknown>) : it);
      } else if (c.itemsJson && typeof c.itemsJson === 'string') {
        try { c.items = JSON.parse(c.itemsJson).map((it: any) => typeof it === 'object' && it !== null ? snakeToCamel(it as Record<string, unknown>) : it); } catch { c.items = []; }
      } else {
        c.items = [];
      }
      // Convert nested review history and discussion
      if (Array.isArray(c.reviewHistory)) {
        c.reviewHistory = c.reviewHistory.map((rh: any) => typeof rh === 'object' && rh !== null ? snakeToCamel(rh as Record<string, unknown>) : rh);
      } else { c.reviewHistory = []; }
      if (Array.isArray(c.discussion)) {
        c.discussion = c.discussion.map((d: any) => typeof d === 'object' && d !== null ? snakeToCamel(d as Record<string, unknown>) : d);
      } else { c.discussion = []; }
      return c as CampaignSubmission;
    });
  }

  async saveCampaignSubmission(submission: CampaignSubmission): Promise<void> {
    const body = camelToSnake(submission as unknown as Record<string, unknown>);
    await apiFetch('/api/aggregator/submissions', { method: 'POST', body: JSON.stringify(body) });
  }

  async deleteCampaignSubmission(id: string): Promise<void> {
    await apiFetch(`/api/aggregator/submissions/${id}`, { method: 'DELETE' });
  }

  // ── Brands ────────────────────────────────────────────────────────────────

  async getBrands(): Promise<Brand[]> {
    const res = await apiFetch<ApiResponse<Record<string, unknown>[]>>('/api/brands');
    return (res.data ?? []).map(normalizeApiBrand);
  }

  async saveBrand(brand: Brand): Promise<void> {
    const body = camelToSnake(brand as unknown as Record<string, unknown>);
    if (brand.id) {
      await putWithCreateFallback(`/api/brands/${brand.id}`, '/api/brands', body);
      return;
    }

    await apiFetch('/api/brands', { method: 'POST', body: JSON.stringify(body) });
  }

  async deleteBrand(id: string): Promise<void> {
    await apiFetch(`/api/brands/${id}`, { method: 'DELETE' });
  }

  // ── Departments ───────────────────────────────────────────────────────────

  async getDepartments(): Promise<Department[]> {
    const res = await apiFetch<ApiResponse<Record<string, unknown>[]>>('/api/departments');
    return (res.data ?? []).map(r => snakeToCamel(r) as unknown as Department);
  }

  async saveDepartment(dept: Department): Promise<void> {
    const body = camelToSnake(dept as unknown as Record<string, unknown>);
    await apiFetch('/api/departments', { method: 'POST', body: JSON.stringify(body) });
  }

  async updateDepartment(dept: Department): Promise<void> {
    const body = camelToSnake(dept as unknown as Record<string, unknown>);
    await apiFetch(`/api/departments/${dept.id}`, { method: 'PUT', body: JSON.stringify(body) });
  }

  async deleteDepartment(id: string): Promise<void> {
    await apiFetch(`/api/departments/${id}`, { method: 'DELETE' });
  }

  // ── Module Access Requests ────────────────────────────────────────────────

  async getModuleRequests(_orgId?: string): Promise<ModuleRequest[]> {
    const res = await apiFetch<ApiResponse<Record<string, unknown>[]>>('/api/module-requests');
    return (res.data ?? []).map(r => snakeToCamel(r) as unknown as ModuleRequest);
  }

  async saveModuleRequest(request: ModuleRequest): Promise<void> {
    const body = camelToSnake(request as unknown as Record<string, unknown>);
    await apiFetch('/api/module-requests', { method: 'POST', body: JSON.stringify(body) });
  }

  // ── Subscription Plans ────────────────────────────────────────────────────

  async getSubscriptionPlans(): Promise<SubscriptionPlan[]> {
    try {
      const res = await apiFetch<ApiResponse<Record<string, unknown>[]>>('/api/subscription-plans');
      return (res.data ?? []).map(normalizeSubscriptionPlan);
    } catch {
      return [];
    }
  }

  async saveSubscriptionPlan(plan: SubscriptionPlan): Promise<void> {
    const body = camelToSnake(plan as unknown as Record<string, unknown>);
    await apiFetch('/api/subscription-plans', { method: 'POST', body: JSON.stringify(body) });
  }

  async deleteSubscriptionPlan(id: string): Promise<void> {
    await apiFetch(`/api/subscription-plans/${id}`, { method: 'DELETE' });
  }

  async getPlatformSettings(): Promise<PlatformSettings> {
    try {
      const res = await apiFetch<ApiResponse<Record<string, unknown>>>('/api/platform-settings');
      const settings = snakeToCamel(res.data) as Record<string, unknown>;
      return {
        platformName: String(settings.platformName ?? 'ClickIt Platform'),
        allowSignups: !!settings.allowSignups,
        autoApproveNewOrganizations: !!settings.autoApproveNewOrganizations,
        updatedAt: typeof settings.updatedAt === 'number' ? settings.updatedAt : undefined,
        logoUrl: typeof settings.logoUrl === 'string' ? settings.logoUrl : undefined,
        faviconUrl: typeof settings.faviconUrl === 'string' ? settings.faviconUrl : undefined,
        heroTitle: typeof settings.heroTitle === 'string' ? settings.heroTitle : undefined,
        heroDescription: typeof settings.heroDescription === 'string' ? settings.heroDescription : undefined,
        heroImageUrl: typeof settings.heroImageUrl === 'string' ? settings.heroImageUrl : undefined,
        newsItems: Array.isArray(settings.newsItems) ? settings.newsItems as PlatformSettings['newsItems'] : undefined,
        loginWelcomeText: typeof settings.loginWelcomeText === 'string' ? settings.loginWelcomeText : undefined,
        loginSidePanelImageUrl: typeof settings.loginSidePanelImageUrl === 'string' ? settings.loginSidePanelImageUrl : undefined,
        aiFeaturesEnabled: !!settings.aiFeaturesEnabled,
        aiTranslationPersona: typeof settings.aiTranslationPersona === 'string' ? settings.aiTranslationPersona : undefined,
        aiModel: typeof settings.aiModel === 'string' ? settings.aiModel : undefined,
        aiProvider: typeof settings.aiProvider === 'string' ? settings.aiProvider : undefined,
        aiApiKey: undefined,
        aiApiKeyConfigured: !!settings.aiApiKeyConfigured,
      };
    } catch {
      return {
        platformName: 'ClickIt Platform',
        allowSignups: true,
        autoApproveNewOrganizations: false,
      };
    }
  }

  async savePlatformSettings(settings: PlatformSettings): Promise<PlatformSettings> {
    const body = camelToSnake(settings as unknown as Record<string, unknown>);
    const res = await apiFetch<ApiResponse<Record<string, unknown>>>('/api/platform-settings', { method: 'PUT', body: JSON.stringify(body) });
    const d = snakeToCamel(res.data ?? {}) as Record<string, unknown>;
    return {
      platformName: String(d.platformName ?? settings.platformName),
      allowSignups: d.allowSignups != null ? !!d.allowSignups : settings.allowSignups,
      autoApproveNewOrganizations: d.autoApproveNewOrganizations != null ? !!d.autoApproveNewOrganizations : settings.autoApproveNewOrganizations,
      updatedAt: typeof d.updatedAt === 'number' ? d.updatedAt : settings.updatedAt,
      logoUrl: typeof d.logoUrl === 'string' ? d.logoUrl : settings.logoUrl,
      faviconUrl: typeof d.faviconUrl === 'string' ? d.faviconUrl : settings.faviconUrl,
      heroTitle: typeof d.heroTitle === 'string' ? d.heroTitle : settings.heroTitle,
      heroDescription: typeof d.heroDescription === 'string' ? d.heroDescription : settings.heroDescription,
      heroImageUrl: typeof d.heroImageUrl === 'string' ? d.heroImageUrl : settings.heroImageUrl,
      newsItems: Array.isArray(d.newsItems) ? d.newsItems as PlatformSettings['newsItems'] : settings.newsItems,
      loginWelcomeText: typeof d.loginWelcomeText === 'string' ? d.loginWelcomeText : settings.loginWelcomeText,
      loginSidePanelImageUrl: typeof d.loginSidePanelImageUrl === 'string' ? d.loginSidePanelImageUrl : settings.loginSidePanelImageUrl,
      aiFeaturesEnabled: d.aiFeaturesEnabled != null ? !!d.aiFeaturesEnabled : settings.aiFeaturesEnabled,
      aiTranslationPersona: typeof d.aiTranslationPersona === 'string' ? d.aiTranslationPersona : settings.aiTranslationPersona,
      aiModel: typeof d.aiModel === 'string' ? d.aiModel : settings.aiModel,
      aiProvider: typeof d.aiProvider === 'string' ? d.aiProvider : settings.aiProvider,
      aiApiKey: typeof d.aiApiKey === 'string' ? d.aiApiKey : undefined,
      aiApiKeyConfigured: !!d.aiApiKeyConfigured,
    };
  }

  // ── Module Quotas ─────────────────────────────────────────────────────────

  async getModuleQuotas(): Promise<ModuleQuotaConfig[]> {
    try {
      const res = await apiFetch<ApiResponse<Record<string, unknown>[]>>('/api/module-quotas');
      return (res.data ?? []).map(normalizeModuleQuota);
    } catch {
      return [];
    }
  }

  async saveModuleQuota(config: ModuleQuotaConfig): Promise<void> {
    const body = {
      id: config.id,
      plan_id: config.planId,
      module_id: config.moduleId,
      limits: normalizeNumericLimits(config.limits),
    };
    await apiFetch('/api/module-quotas', { method: 'POST', body: JSON.stringify(body) });
  }

  async deleteModuleQuota(id: string): Promise<void> {
    await apiFetch(`/api/module-quotas/${id}`, { method: 'DELETE' });
  }

  // ── Platform Features ─────────────────────────────────────────────────────

  async getPlatformFeatures(): Promise<PlatformFeature[]> {
    try {
      const res = await apiFetch<ApiResponse<Record<string, unknown>[]>>('/api/platform-features');
      return (res.data ?? []).map(r => snakeToCamel(r) as unknown as PlatformFeature);
    } catch {
      return [];
    }
  }

  async savePlatformFeature(feature: PlatformFeature): Promise<void> {
    const body = camelToSnake(feature as unknown as Record<string, unknown>);
    await apiFetch('/api/platform-features', { method: 'POST', body: JSON.stringify(body) });
  }

  async deletePlatformFeature(id: string): Promise<void> {
    await apiFetch(`/api/platform-features/${id}`, { method: 'DELETE' });
  }

  // ── Plan Feature Values ───────────────────────────────────────────────────

  async getPlanFeatureValues(): Promise<PlanFeatureValue[]> {
    try {
      const res = await apiFetch<ApiResponse<Record<string, unknown>[]>>('/api/plan-feature-values');
      return (res.data ?? []).map(r => snakeToCamel(r) as unknown as PlanFeatureValue);
    } catch {
      return [];
    }
  }

  async savePlanFeatureValue(val: PlanFeatureValue): Promise<void> {
    const body = camelToSnake(val as unknown as Record<string, unknown>);
    await apiFetch('/api/plan-feature-values', { method: 'POST', body: JSON.stringify(body) });
  }

  async deletePlanFeatureValue(id: string): Promise<void> {
    await apiFetch(`/api/plan-feature-values/${id}`, { method: 'DELETE' });
  }

  // ── Media Upload (R2) ────────────────────────────────────────────────────

  async uploadMedia(file: File, moduleId?: string, entityId?: string): Promise<{ key: string; url: string }> {
    const token = getAuthToken();
    const formData = new FormData();
    formData.append('file', file);
    if (moduleId) formData.append('module', moduleId);
    if (entityId) formData.append('entityId', entityId);

    const controller = new AbortController();
    const timeoutId = window.setTimeout(() => controller.abort(), 45000);
    let res: Response;
    try {
      res = await fetch(`${API_BASE}/api/media/upload`, {
        method: 'POST',
        headers: token ? { 'Authorization': `Bearer ${token}` } : {},
        body: formData,
        signal: controller.signal,
      });
    } catch (error: any) {
      if (error?.name === 'AbortError') {
        throw new ApiError(0, 'Image upload failed. Item not saved.');
      }
      throw new ApiError(0, error?.message || 'Image upload failed. Item not saved.');
    } finally {
      window.clearTimeout(timeoutId);
    }

    if (!res.ok) {
      const body = await res.json().catch(() => ({ message: 'Upload failed' }));
      throw new ApiError(res.status, (body as any).message ?? 'Upload failed');
    }

    return res.json();
  }
}
